import React, { useState, useRef } from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { GENERATE_REPORT_DATA, GET_REPORTS, GET_REPORT, DELETE_REPORT, SAVE_REPORT_DATA, UPDATE_REPORT_NAME } from '../graphql/reports';
import { GET_SERVICE_TYPES } from '../graphql/queries';
import { generatePayrollReportName, generateServiceReportName, generateWorkOrderReportName } from '../utils/reportNaming';
import { useAuth } from '../contexts/AuthContext';
import { parseManagementFilter } from '../utils/propertyManagers';

// Import report components
import {
  ReportTypeSelector,
  ReportType,
  RoleFilterSelector,
  RoleFilter,
  DateRangeSelector,
  AdvancedFiltersSection,
  ReportView,
  ServiceReportView,
  JobStatus,
  PayrollAreaSelector,
  WorkOrderReportView,
  WorkOrderFilterSection,
  WorkOrderStatus
} from '../components/reports';

// Safe date formatting function to prevent "Invalid time value" errors
const safeFormatDate = (dateValue, formatPattern = 'MMM dd, yyyy', fallback = 'Invalid date') => {
  if (!dateValue) return fallback;
  
  try {
    let date;
    
    // Handle different date value types
    if (typeof dateValue === 'string') {
      if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // If it's a YYYY-MM-DD string, add time component to avoid timezone shift
        date = new Date(dateValue + 'T00:00:00');
      } else if (dateValue.includes('T')) {
        // Handle ISO date strings
        date = new Date(dateValue);
      } else if (/^\d+$/.test(dateValue)) {
        // Handle numeric timestamp strings like "1759708800000" (from production bug)
        // Extract UTC date and create local date to avoid timezone shift
        const tempDate = new Date(parseInt(dateValue, 10));
        const year = tempDate.getUTCFullYear();
        const month = tempDate.getUTCMonth();
        const day = tempDate.getUTCDate();
        date = new Date(year, month, day); // Create as local date
      } else {
        // Try parsing as regular date string
        date = new Date(dateValue);
      }
    } else if (typeof dateValue === 'number') {
      // Handle timestamp values (milliseconds since epoch)
      // Create date in UTC to avoid timezone shifts
      date = new Date(dateValue);
      // For date-only values (no time component), interpret as UTC date
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const day = date.getUTCDate();
      date = new Date(year, month, day); // Create as local date
    } else if (dateValue instanceof Date) {
      // Already a Date object
      date = dateValue;
    } else {
      // Try to create a date from the value
      date = new Date(dateValue);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date detected:', dateValue, 'Type:', typeof dateValue);
      return fallback;
    }
    
    return format(date, formatPattern);
  } catch (error) {
    console.error('Error formatting date:', dateValue, error);
    return fallback;
  }
};

export default function Reports({ section = 'general' }) {
  const navigate = useNavigate();
  // Optional path param - when present, this report is loaded on mount and
  // the URL stays in sync as the user generates / opens / closes reports.
  // Lets users deep-link, share URLs, and use the browser back button.
  const { reportId: urlReportId } = useParams();
  const { user } = useAuth();
  const isWorkOrdersSection = section === 'work-orders';
  const reportsBasePath = isWorkOrdersSection
    ? '/dashboard/reports/work-orders'
    : '/dashboard/reports';
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'GUEST_APP_MANAGER';
  const canManageQuickBooks = user?.role === 'ADMIN' || user?.role === 'PROPERTY_MANAGER';
  const [reportType, setReportType] = useState(ReportType.HOUSEKEEPING);
  const [roleFilter, setRoleFilter] = useState(RoleFilter.ALL);
  const [startDate, setStartDate] = useState(() => safeFormatDate(new Date(), 'yyyy-MM-dd', format(new Date(), 'yyyy-MM-dd')));
  const [endDate, setEndDate] = useState(() => safeFormatDate(new Date(), 'yyyy-MM-dd', format(new Date(), 'yyyy-MM-dd')));
  const [reportData, setReportData] = useState(null);
  const [savedReportId, setSavedReportId] = useState(null);
  const [savedReportName, setSavedReportName] = useState(null);
  const [isAdvancedReport, setIsAdvancedReport] = useState(false);
  
  // State for payroll area filtering
  const [selectedPayrollAreas, setSelectedPayrollAreas] = useState([]);
  
  // State for inline editing of report names
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingReportName, setEditingReportName] = useState('');

  // Accordion state for all sections
  const [showPayrollReports, setShowPayrollReports] = useState(false);
  const [showServiceReports, setShowServiceReports] = useState(false);
  const [showWorkOrderReports, setShowWorkOrderReports] = useState(isWorkOrdersSection);

  // Saved reports tab state — PM defaults to work orders
  const [savedReportsTab, setSavedReportsTab] = useState(
    isWorkOrdersSection ? 'workorders' : 'housekeeping'
  );

  // Work Orders report state
  const [workOrderStartDate, setWorkOrderStartDate] = useState(() => {
    const last30Days = new Date();
    last30Days.setDate(last30Days.getDate() - 30);
    return format(last30Days, 'yyyy-MM-dd');
  });
  const [workOrderEndDate, setWorkOrderEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [selectedWorkOrderStatuses, setSelectedWorkOrderStatuses] = useState([WorkOrderStatus.COMPLETED]);
  const [selectedWorkOrderDepartments, setSelectedWorkOrderDepartments] = useState([]);
  const [selectedWorkOrderAreas, setSelectedWorkOrderAreas] = useState([]);
  const [selectedWorkOrderCustomers, setSelectedWorkOrderCustomers] = useState([]);
  const [selectedWorkOrderProperties, setSelectedWorkOrderProperties] = useState([]);
  const [selectedWorkOrderPropertyManagement, setSelectedWorkOrderPropertyManagement] = useState('');

  // New state for advanced filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedJobStatuses, setSelectedJobStatuses] = useState([]);
  const [isRoleFilterEnabled, setIsRoleFilterEnabled] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  // Advanced filter date range (separate from basic report date range)
  const [advancedStartDate, setAdvancedStartDate] = useState(() => {
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    return format(lastWeek, 'yyyy-MM-dd');
  });
  const [advancedEndDate, setAdvancedEndDate] = useState(() => safeFormatDate(new Date(), 'yyyy-MM-dd', format(new Date(), 'yyyy-MM-dd')));

  // State to track applied filters for report naming
  const [appliedFilters, setAppliedFilters] = useState({});

  // Ref to store report parameters for auto-save after generation
  const pendingReportParamsRef = useRef(null);

  // Latest WORK_ORDERS query input. Used to re-run the live query after
  // every entity mutation made inside WorkOrderReportView (toggle exclusion,
  // edit time, mark expense unbillable, ...). The Work Order report is a
  // "live view" - filters are saved, the data isn't.
  const lastWorkOrderInputRef = useRef(null);

  const [generateReportData, { loading: isGenerating, error: generateError }] = useLazyQuery(
    GENERATE_REPORT_DATA,
    {
      onCompleted: async (data) => {
        // Normalize the data - handle aliased fields
        const normalizedData = { ...data.generateReportData };
        
        // If payrollAppliedAreas exists (from ReportDataResponse), map it to appliedAreas
        if (normalizedData.payrollAppliedAreas !== undefined) {
          normalizedData.appliedAreas = normalizedData.payrollAppliedAreas;
          delete normalizedData.payrollAppliedAreas;
        }
        
        // Auto-save BEFORE setting report data so savedReportId is available
        // when the report view component mounts (prevents duplicate creation).
        // WORK_ORDERS reports skip the auto-save - the user clicks "Save
        // Report" explicitly when they want to keep the filter/date-range
        // for re-use. Entity edits (billable toggles, descriptions, etc.)
        // persist directly to the work orders regardless of whether the
        // report row itself is saved.
        if (pendingReportParamsRef.current) {
          if (pendingReportParamsRef.current.type !== 'WORK_ORDERS') {
            await autoSaveGeneratedReport(normalizedData, pendingReportParamsRef.current);
          }
          pendingReportParamsRef.current = null;
        }

        setReportData(normalizedData);
      },
      onError: (error) => {
        console.error('Error generating report:', error);
        alert('Failed to generate report. Please try again.');
        setSavedReportId(null);
        setSavedReportName(null);
        pendingReportParamsRef.current = null;
      }
    }
  );

  const [getReport] = useLazyQuery(GET_REPORT, {
    onCompleted: (data) => {
      if (data.report) {
        handleLoadSavedReportData(data.report);
      }
    },
    onError: (error) => {
      console.error('Error loading saved report:', error);
      // If we tried to load from URL and the report no longer exists
      // (or the user can't access it), bounce back to the picker.
      navigate(reportsBasePath, { replace: true });
      alert('Failed to load saved report. Please try again.');
    }
  });

  const [deleteReport] = useMutation(DELETE_REPORT, {
    onCompleted: () => {
      // Removed success alert - user already confirmed deletion
    },
    onError: (error) => {
      console.error('Error deleting report:', error);
      alert('Failed to delete report. Please try again.');
    },
    refetchQueries: [{ query: GET_REPORTS }],
    awaitRefetchQueries: true
  });

  const [saveReportData, { loading: isSavingAutoReport }] = useMutation(SAVE_REPORT_DATA, {
    refetchQueries: [{ query: GET_REPORTS }],
    onCompleted: (data) => {
      if (data.saveReportData && data.saveReportData.id) {
        setSavedReportId(data.saveReportData.id);
        // Reflect the saved report in the URL using replace() so we don't
        // pollute the back stack - the user can still go back to wherever
        // they came from (e.g. dashboard) with a single press.
        if (urlReportId !== data.saveReportData.id) {
          navigate(`${reportsBasePath}/${data.saveReportData.id}`, { replace: true });
        }
        toast.success(`Report auto-saved: "${data.saveReportData.name}"`);
      }
    },
    onError: (error) => {
      console.error('Error auto-saving report:', error);
      toast.error('Report generated but auto-save failed. Please save manually.');
    }
  });

  const [updateReportName, { loading: isUpdatingName }] = useMutation(UPDATE_REPORT_NAME, {
    refetchQueries: [{ query: GET_REPORTS }],
    onCompleted: () => {
      toast.success('Report name updated successfully');
      setEditingReportId(null);
      setEditingReportName('');
    },
    onError: (error) => {
      console.error('Error updating report name:', error);
      toast.error('Failed to update report name. Please try again.');
    }
  });

  const { data: savedReports, loading: savedReportsLoading, error: savedReportsError } = useQuery(GET_REPORTS, {
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      console.error('Error loading saved reports:', error);
    }
  });

  // Get service types for better report naming
  const { data: serviceTypesData } = useQuery(GET_SERVICE_TYPES);

  // URL ↔ state sync.
  //
  // When the route's `:reportId` changes (mount, deep link, browser back/
  // forward, Reports nav click), reconcile it with the in-memory state:
  // - id in URL & not yet loaded → fetch and render it.
  // - no id in URL & a report is currently displayed → return to the picker
  //   without the user having to click "Back to Selection".
  //
  // Only depends on the URL value so it doesn't fire on every state change.
  React.useEffect(() => {
    if (urlReportId) {
      if (savedReportId !== urlReportId) {
        getReport({ variables: { id: urlReportId } });
      }
      return;
    }
    if (savedReportId || reportData) {
      setReportData(null);
      setSavedReportId(null);
      setSavedReportName(null);
      setIsAdvancedReport(false);
      setSelectedPayrollAreas([]);
      setAppliedFilters({});
      pendingReportParamsRef.current = null;
      lastWorkOrderInputRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlReportId]);

  /**
   * Automatically saves a newly generated report with a default name
   * This prevents data loss if user loses connection while editing
   */
  const autoSaveGeneratedReport = async (reportData, reportParams) => {
    try {
      // Generate appropriate default name based on report type
      let defaultName;
      
      if (reportParams.isAdvanced) {
        // For service-based reports
        const areas = reportParams.filters?.areas || [];
        defaultName = generateServiceReportName(areas);
        
        // Append applied filters to name if any
        if (reportParams.appliedFilters && Object.keys(reportParams.appliedFilters).length > 0) {
          const filterDescriptions = [];
          
          if (reportParams.appliedFilters.serviceTypes?.length > 0) {
            filterDescriptions.push(reportParams.appliedFilters.serviceTypes.join(', '));
          }
          if (reportParams.appliedFilters.properties?.length > 0) {
            const propNames = reportParams.appliedFilters.properties.slice(0, 2).join(', ');
            const additional = reportParams.appliedFilters.properties.length > 2 
              ? ` +${reportParams.appliedFilters.properties.length - 2} more` 
              : '';
            filterDescriptions.push(propNames + additional);
          }
          if (reportParams.appliedFilters.teamMembers?.length > 0) {
            const memberNames = reportParams.appliedFilters.teamMembers.slice(0, 2).join(', ');
            const additional = reportParams.appliedFilters.teamMembers.length > 2 
              ? ` +${reportParams.appliedFilters.teamMembers.length - 2} more` 
              : '';
            filterDescriptions.push(memberNames + additional);
          }
          
          if (filterDescriptions.length > 0) {
            defaultName = `${defaultName} - ${filterDescriptions.join(' | ')}`;
          }
        }
      } else if (reportParams.type === 'WORK_ORDERS') {
        // For work order reports
        const areas = reportParams.filters?.areas || [];
        defaultName = generateWorkOrderReportName(areas);
      } else {
        // For payroll reports
        const areas = reportParams.filters?.areas || reportParams.selectedAreas || [];
        defaultName = generatePayrollReportName(
          reportParams.type, 
          reportParams.roleFilter,
          areas
        );
      }
      
      // Add date range to name - use T00:00:00 to parse as local time (prevents timezone shift)
      const formattedStartDate = format(new Date(reportParams.startDate + 'T00:00:00'), 'MMM dd, yyyy');
      const formattedEndDate = format(new Date(reportParams.endDate + 'T00:00:00'), 'MMM dd, yyyy');
      defaultName = `${defaultName} - ${formattedStartDate} to ${formattedEndDate}`;

      // WORK_ORDERS reports save filters only (live view model). All other
      // report types continue to persist the full data snapshot in JSON.
      const isWorkOrderQuery = reportParams.type === 'WORK_ORDERS';
      const persistedJson = isWorkOrderQuery
        ? JSON.stringify({ filters: reportParams.filters || {} })
        : JSON.stringify(reportData);

      const saveInput = {
        name: defaultName,
        existingReportId: null,
        reportDataJson: persistedJson,
        type: reportParams.type,
        startDate: reportParams.startDate,
        endDate: reportParams.endDate,
        roleFilter: reportParams.roleFilter || null,
        totalPayout: isWorkOrderQuery ? 0 : (reportData.grandTotalPayout || 0),
        totalCost: isWorkOrderQuery ? 0 : (reportData.grandTotalBilledAmount || 0),
        totalExpenses: isWorkOrderQuery ? 0 : (reportData.totalExpenses || 0),
        finalTotal: isWorkOrderQuery ? 0 : (reportData.finalTotal || 0),
        jobsCount: isWorkOrderQuery ? (reportData?.summary?.totalWorkOrders || 0) : (reportData.totalJobs || 0)
      };

      await saveReportData({
        variables: { input: saveInput }
      });
      
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Don't throw - let user continue with unsaved report
    }
  };

  const handleReportTypeChange = (newReportType) => {
    setReportType(newReportType);
    // Reset role filter when changing report type
    if (newReportType !== ReportType.INSPECTOR_PROPERTY_MANAGER) {
      setRoleFilter(RoleFilter.ALL);
    }
    // Reset area filters when changing report type
    setSelectedPayrollAreas([]);
  };

  const handleLoadSavedReportData = (report) => {
    try {
      // Keep legacy saved Work Order links working, then move them onto the
      // dedicated route so Back, Save, and refresh all stay in that section.
      if (report.type === ReportType.WORK_ORDERS && !isWorkOrdersSection) {
        navigate(`/dashboard/reports/work-orders/${report.id}`, { replace: true });
        return;
      }

      let rawParsedData = JSON.parse(report.reportDataJson || '{}');

      if (typeof rawParsedData === 'string') {
        console.log('🔧 Detected double-stringified data, parsing again...');
        rawParsedData = JSON.parse(rawParsedData);
      }

      const cleanData = (obj) => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(cleanData);
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (key !== '__typename') {
            cleaned[key] = cleanData(value);
          }
        }
        return cleaned;
      };

      const parsedData = cleanData(rawParsedData);

      const parsedStartDate = safeFormatDate(report.startDate, 'yyyy-MM-dd', format(new Date(), 'yyyy-MM-dd'));
      const parsedEndDate = safeFormatDate(report.endDate, 'yyyy-MM-dd', format(new Date(), 'yyyy-MM-dd'));

      setReportType(report.type);
      setStartDate(parsedStartDate);
      setEndDate(parsedEndDate);
      setSavedReportId(report.id);
      setSavedReportName(report.name);
      setAppliedFilters({});

      // WORK_ORDERS is a live query - the saved blob contains only filters.
      // We re-run the live report generation rather than display the saved
      // payload (which was filter-only and contains no data).
      if (report.type === 'WORK_ORDERS') {
        const filters = (parsedData && parsedData.filters) || {};
        setIsAdvancedReport(false);

        setWorkOrderStartDate(parsedStartDate);
        setWorkOrderEndDate(parsedEndDate);
        setSelectedWorkOrderStatuses(filters.workOrderStatuses || [WorkOrderStatus.COMPLETED]);
        setSelectedWorkOrderDepartments(filters.departments || []);
        setSelectedWorkOrderAreas(filters.areas || []);
        setSelectedWorkOrderCustomers(filters.quickbooksCustomerIds || []);
        // propertyIds were persisted as id strings; the live filter accepts ids.
        setSelectedWorkOrderProperties((filters.propertyIds || []).map(id => ({ id })));
        setSelectedWorkOrderPropertyManagement(
          filters.propertyManagementTeamId
            ? `team:${filters.propertyManagementTeamId}`
            : filters.propertyManagerId
              ? `user:${filters.propertyManagerId}`
              : ''
        );

        const workOrderInput = {
          type: 'WORK_ORDERS',
          startDate: parsedStartDate,
          endDate: parsedEndDate,
          filters: {
            workOrderStatuses: filters.workOrderStatuses || [WorkOrderStatus.COMPLETED],
            departments: filters.departments || [],
            areas: filters.areas && filters.areas.length > 0 ? filters.areas : undefined,
            quickbooksCustomerIds: filters.quickbooksCustomerIds && filters.quickbooksCustomerIds.length > 0 ? filters.quickbooksCustomerIds : undefined,
            propertyIds: filters.propertyIds && filters.propertyIds.length > 0 ? filters.propertyIds : undefined,
            propertyManagerId: filters.propertyManagerId || undefined,
            propertyManagementTeamId: filters.propertyManagementTeamId || undefined
          }
        };
        lastWorkOrderInputRef.current = workOrderInput;
        setReportData(null);
        // Skip auto-save - this report is already saved.
        pendingReportParamsRef.current = null;
        generateReportData({ variables: { input: workOrderInput } });
        return;
      }

      const isServiceBasedReport = report.name.includes('Service Report:') ||
                                  report.name.includes('Service Based Report') ||
                                  report.name.includes('Filtered Report');

      setReportData(parsedData);
      setRoleFilter(report.roleFilter || RoleFilter.ALL);
      setIsAdvancedReport(isServiceBasedReport);

      if (parsedData.appliedAreas && Array.isArray(parsedData.appliedAreas) && parsedData.appliedAreas.length > 0) {
        setSelectedPayrollAreas(parsedData.appliedAreas);
      } else {
        setSelectedPayrollAreas([]);
      }
    } catch (error) {
      console.error('Error parsing saved report data:', error);
      console.error('Report data that caused error:', {
        id: report.id,
        name: report.name,
        startDate: report.startDate,
        endDate: report.endDate,
        createdAt: report.createdAt
      });
      alert('Failed to load saved report. The data may be corrupted.');
    }
  };

  const handleLoadSavedReport = (report) => {
    // Drive the load through the URL so the address bar reflects what the
    // user is viewing and the browser back button returns them to the picker.
    navigate(`${reportsBasePath}/${report.id}`);
  };

  const handleReportSaved = (savedReport) => {
    if (!savedReport) return;
    if (savedReport.id) {
      setSavedReportId(savedReport.id);
    }
    if (savedReport.name) {
      setSavedReportName(savedReport.name);
    }

    // Reflect the saved id in the URL so the user can deep-link or refresh.
    // replace() because the user is already on the report view; we just want
    // the address bar to catch up with what's loaded, not add a history entry.
    if (savedReport.id && urlReportId !== savedReport.id) {
      navigate(`${reportsBasePath}/${savedReport.id}`, { replace: true });
    }

    // WORK_ORDERS reports persist only filters; the report view continues to
    // use the in-memory live data and doesn't need re-hydration after save.
    if (savedReport.type === 'WORK_ORDERS') return;

    // Other report types still persist the full data snapshot; re-hydrate
    // so any server-side normalization is reflected in the view.
    if (savedReport.reportDataJson) {
      try {
        let rawParsedData = JSON.parse(savedReport.reportDataJson || '{}');
        if (typeof rawParsedData === 'string') {
          rawParsedData = JSON.parse(rawParsedData);
        }
        const cleanData = (obj) => {
          if (obj === null || typeof obj !== 'object') return obj;
          if (Array.isArray(obj)) return obj.map(cleanData);
          const cleaned = {};
          for (const [key, value] of Object.entries(obj)) {
            if (key !== '__typename') cleaned[key] = cleanData(value);
          }
          return cleaned;
        };
        setReportData(cleanData(rawParsedData));
      } catch (error) {
        console.error('Error updating report data after save:', error);
      }
    }
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('Start date must be before or equal to end date');
      return;
    }

    const reportInput = {
      type: reportType,
      startDate: startDate, // Send as YYYY-MM-DD string to avoid timezone issues
      endDate: endDate // Send as YYYY-MM-DD string to avoid timezone issues
    };

    // Add role filter for Inspector & Property Manager reports
    if (reportType === ReportType.INSPECTOR_PROPERTY_MANAGER && roleFilter !== RoleFilter.ALL) {
      reportInput.roleFilter = roleFilter;
    }

    // Add area filtering if areas are selected
    if (selectedPayrollAreas.length > 0) {
      reportInput.filters = {
        areas: selectedPayrollAreas
      };
    }

    setIsAdvancedReport(false); // Mark as basic report
    setSavedReportId(null); // Clear saved report ID for new report
    setSavedReportName(null); // Clear saved report name for new report

    // Store report parameters for auto-save after generation
    pendingReportParamsRef.current = {
      type: reportType,
      startDate: startDate,
      endDate: endDate,
      roleFilter: roleFilter,
      isAdvanced: false,
      filters: {
        areas: selectedPayrollAreas
      },
      selectedAreas: selectedPayrollAreas,
      appliedFilters: {}
    };

    generateReportData({
      variables: {
        input: reportInput
      }
    });
  };

  const handleBackToSelection = () => {
    // Navigate to the bare /reports path. The URL ↔ state effect will pick
    // this up and clear the in-memory report; a single source of truth for
    // teardown keeps things consistent with browser back / nav-link clicks.
    if (urlReportId) {
      navigate(reportsBasePath);
      return;
    }
    setReportData(null);
    setSavedReportId(null);
    setSavedReportName(null);
    setIsAdvancedReport(false);
    setSelectedPayrollAreas([]);
    setAppliedFilters({});
    pendingReportParamsRef.current = null;
    lastWorkOrderInputRef.current = null;
  };

  const handleGenerateWorkOrderReport = async (departmentRoles) => {
    if (!workOrderStartDate || !workOrderEndDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(workOrderStartDate) > new Date(workOrderEndDate)) {
      alert('Start date must be before or equal to end date');
      return;
    }

    // Enforce 31-day max for work order reports
    const start = new Date(workOrderStartDate + 'T00:00:00');
    const end = new Date(workOrderEndDate + 'T00:00:00');
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (daysDiff > 31) {
      alert('Work order reports are limited to 31 days at a time. Please narrow your date range.');
      return;
    }

    if (selectedWorkOrderStatuses.length === 0) {
      alert('Please select at least one work order status');
      return;
    }

    const reportInput = {
      type: ReportType.WORK_ORDERS,
      startDate: workOrderStartDate,
      endDate: workOrderEndDate,
      filters: {
        workOrderStatuses: selectedWorkOrderStatuses,
        departments: departmentRoles,
        areas: selectedWorkOrderAreas.length > 0 ? selectedWorkOrderAreas : undefined,
        quickbooksCustomerIds: selectedWorkOrderCustomers.length > 0 ? selectedWorkOrderCustomers : undefined,
        propertyIds: selectedWorkOrderProperties.length > 0 ? selectedWorkOrderProperties.map(p => p.id) : undefined,
        ...parseManagementFilter(selectedWorkOrderPropertyManagement)
      }
    };

    setReportType(ReportType.WORK_ORDERS);
    setIsAdvancedReport(false);
    setSavedReportId(null);
    setSavedReportName(null);

    lastWorkOrderInputRef.current = reportInput;

    // Store report parameters for auto-save after generation. WORK_ORDERS
    // saves filters only, so the same filter shape is persisted on the
    // report row and used to re-hydrate the saved query on load.
    pendingReportParamsRef.current = {
      type: ReportType.WORK_ORDERS,
      startDate: workOrderStartDate,
      endDate: workOrderEndDate,
      isAdvanced: false,
      filters: {
        workOrderStatuses: selectedWorkOrderStatuses,
        departments: departmentRoles,
        areas: selectedWorkOrderAreas,
        quickbooksCustomerIds: selectedWorkOrderCustomers,
        propertyIds: selectedWorkOrderProperties.map(p => p.id),
        ...parseManagementFilter(selectedWorkOrderPropertyManagement)
      },
      appliedFilters: {}
    };

    generateReportData({
      variables: {
        input: reportInput
      }
    });
  };

  // Re-run the live Work Order query using the most recent input. Called by
  // WorkOrderReportView after every entity mutation so the view stays
  // consistent with the database. Forces a network round-trip - without
  // this, Apollo's default cache-first policy returns the prior result and
  // newly-created expenses/time entries don't appear until a page refresh.
  const refetchWorkOrderReport = async () => {
    if (!lastWorkOrderInputRef.current) return;
    pendingReportParamsRef.current = null;
    await generateReportData({
      variables: { input: lastWorkOrderInputRef.current },
      fetchPolicy: 'network-only'
    });
  };

  // New handlers for advanced filters
  const handleServiceTypeChange = (serviceTypes) => {
    setSelectedServiceTypes(serviceTypes);
  };

  const handleAreaChange = (areas) => {
    setSelectedAreas(areas);
  };

  const handleJobStatusChange = (statuses) => {
    setSelectedJobStatuses(statuses);
  };

  const handleRoleFilterToggle = (enabled) => {
    setIsRoleFilterEnabled(enabled);
  };

  const handleSelectedPropertiesChange = (properties) => {
    setSelectedProperties(properties);
  };

  const handleSelectedTeamMembersChange = (teamMembers) => {
    setSelectedTeamMembers(teamMembers);
  };

  const resetAdvancedFilters = () => {
    setSearchTerm('');
    setSelectedServiceTypes([]);
    setSelectedAreas([]);
    setSelectedJobStatuses([]);
    setIsRoleFilterEnabled(false);
    setSelectedProperties([]);
    setSelectedTeamMembers([]);
    // Reset advanced filter dates to last 7 days (to match initial state)
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    setAdvancedStartDate(safeFormatDate(lastWeek, 'yyyy-MM-dd', format(new Date(), 'yyyy-MM-dd')));
    setAdvancedEndDate(safeFormatDate(new Date(), 'yyyy-MM-dd', format(new Date(), 'yyyy-MM-dd')));
  };

  const handleAdvancedReportGeneration = async () => {
    if (!advancedStartDate || !advancedEndDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(advancedStartDate) > new Date(advancedEndDate)) {
      alert('Start date must be before or equal to end date');
      return;
    }

    const reportInput = {
      type: reportType,
      startDate: advancedStartDate, // Send as YYYY-MM-DD string to avoid timezone issues
      endDate: advancedEndDate // Send as YYYY-MM-DD string to avoid timezone issues
    };

    // Add role filter for Inspector & Property Manager reports
    if (reportType === ReportType.INSPECTOR_PROPERTY_MANAGER && roleFilter !== RoleFilter.ALL) {
      reportInput.roleFilter = roleFilter;
    }

    // Track applied filters for report naming
    const filtersForNaming = {};

    // Add advanced filters if any are selected
    if (searchTerm || selectedServiceTypes.length > 0 || selectedAreas.length > 0 || selectedProperties.length > 0 || selectedTeamMembers.length > 0 || selectedJobStatuses.length > 0) {
      reportInput.filters = {};
      
      // If searchTerm is just text (not from a specific suggestion), use it as searchTerm
      // If it's from a specific suggestion, we'll handle it differently
      if (searchTerm) {
        reportInput.filters.searchTerm = searchTerm;
        filtersForNaming.searchTerm = searchTerm;
      }
      
      if (selectedServiceTypes.length > 0) {
        reportInput.filters.serviceTypeIds = selectedServiceTypes;
        // Map service type IDs to names for better report naming
        const serviceTypeNames = selectedServiceTypes.map(id => {
          const serviceType = serviceTypesData?.serviceTypes?.find(st => st.id === id);
          return serviceType ? serviceType.title : id;
        });
        filtersForNaming.serviceTypes = serviceTypeNames;
      }
      
      if (selectedAreas.length > 0) {
        reportInput.filters.areas = selectedAreas;
        filtersForNaming.areas = selectedAreas;
      }

      if (selectedProperties.length > 0) {
        reportInput.filters.propertyIds = selectedProperties.map(p => p.id);
        filtersForNaming.properties = selectedProperties.map(p => p.name);
      }

      if (selectedTeamMembers.length > 0) {
        reportInput.filters.teamMemberIds = selectedTeamMembers.map(tm => tm.id);
        filtersForNaming.teamMembers = selectedTeamMembers.map(tm => `${tm.firstName} ${tm.lastName}`);
      }

      if (selectedJobStatuses.length > 0) {
        reportInput.filters.jobStatuses = selectedJobStatuses;
        filtersForNaming.jobStatuses = selectedJobStatuses;
      }
    }

    setIsAdvancedReport(true);
    setAppliedFilters(filtersForNaming); // Store filters for report naming
    setSavedReportId(null); // Clear saved report ID for new report
    setSavedReportName(null); // Clear saved report name for new report

    // Store report parameters for auto-save after generation
    pendingReportParamsRef.current = {
      type: reportType,
      startDate: advancedStartDate,
      endDate: advancedEndDate,
      roleFilter: roleFilter,
      isAdvanced: true,
      filters: {
        areas: selectedAreas,
        serviceTypes: selectedServiceTypes,
        properties: selectedProperties,
        teamMembers: selectedTeamMembers,
        jobStatuses: selectedJobStatuses
      },
      appliedFilters: filtersForNaming,
      selectedAreas: []
    };

    generateReportData({
      variables: {
        input: reportInput
      }
    });
  };

  // Inline editing handlers for report names
  const handleStartEditingName = (reportId, currentName) => {
    setEditingReportId(reportId);
    setEditingReportName(currentName);
  };

  const handleReportNameChange = (e) => {
    setEditingReportName(e.target.value);
  };

  const handleSaveReportName = async (reportId) => {
    const trimmedName = editingReportName.trim();
    
    if (!trimmedName) {
      toast.error('Report name cannot be empty');
      return;
    }

    if (trimmedName.length > 255) {
      toast.error('Report name cannot exceed 255 characters');
      return;
    }

    try {
      await updateReportName({
        variables: {
          input: {
            id: reportId,
            name: trimmedName
          }
        }
      });
    } catch (error) {
      // Error is handled by mutation's onError callback
    }
  };

  const handleCancelEditingName = () => {
    setEditingReportId(null);
    setEditingReportName('');
  };

  const handleKeyDownEditName = (e, reportId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveReportName(reportId);
    } else if (e.key === 'Escape') {
      handleCancelEditingName();
    }
  };

  const handleDeleteReport = async (reportId, reportName) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to permanently delete the report "${reportName}"?\n\nThis action cannot be undone.`
    );
    
    if (isConfirmed) {
      try {
        await deleteReport({
          variables: { id: reportId }
        });
        // If we just deleted the report that's currently open via URL, fall
        // back to the picker so the view doesn't show a stale row.
        if (urlReportId === reportId) {
          navigate(reportsBasePath, { replace: true });
        }
      } catch (error) {
        console.error('Error deleting report:', error);
      }
    }
  };

  if (reportData) {
    // Determine which report component to use
    let ReportComponent;
    let reportStartDate;
    let reportEndDate;
    
    if (reportType === ReportType.WORK_ORDERS) {
      ReportComponent = WorkOrderReportView;
      reportStartDate = workOrderStartDate;
      reportEndDate = workOrderEndDate;
    } else if (isAdvancedReport) {
      ReportComponent = ServiceReportView;
      reportStartDate = advancedStartDate;
      reportEndDate = advancedEndDate;
    } else {
      ReportComponent = ReportView;
      reportStartDate = startDate;
      reportEndDate = endDate;
    }
    
    // Create area filter information for report display
    const reportAreaFilters = selectedPayrollAreas.length > 0 ? selectedPayrollAreas : null;
    
    const isWorkOrderReport = reportType === ReportType.WORK_ORDERS;
    const workOrderAppliedFilters = isWorkOrderReport
      ? (lastWorkOrderInputRef.current?.filters || {})
      : {};

    return (
      <ReportComponent
        reportData={reportData}
        reportType={reportType}
        roleFilter={roleFilter}
        startDate={reportStartDate}
        endDate={reportEndDate}
        savedReportId={savedReportId}
        savedReportName={savedReportName}
        onBackToSelection={handleBackToSelection}
        appliedFilters={isWorkOrderReport ? workOrderAppliedFilters : (isAdvancedReport ? appliedFilters : {})}
        selectedAreas={reportAreaFilters}
        onReportSaved={handleReportSaved}
        onRefetch={isWorkOrderReport ? refetchWorkOrderReport : undefined}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white lg:text-gray-900">
            {isWorkOrdersSection ? 'Work Order Reports' : 'Service & Expense Reports'}
          </h1>
          <p className="text-gray-300 lg:text-gray-600">
            {isWorkOrdersSection
              ? 'Generate work order reports with expense tracking and status filtering'
              : 'Generate reports for completed jobs and team performance'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {isWorkOrdersSection && canManageQuickBooks && (
            <button
              onClick={() => navigate('/dashboard/quickbooks-assignments')}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-label="QuickBooks Assignments"
              tabIndex={0}
            >
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              QB Assignments
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => navigate('/dashboard/preset-expenses')}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Preset Expenses & Hourly Employees
            </button>
          )}
        </div>
      </div>

      {/* Payroll and Expense Reports — Admin/Guest App Manager only */}
      {!isWorkOrdersSection && isAdmin && (
        <div className="bg-white rounded-lg shadow">
          <div 
            className="bg-sky-900 text-white p-6 cursor-pointer hover:bg-sky-800 transition-colors"
            onClick={() => setShowPayrollReports(!showPayrollReports)}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Payroll and Expense Reports</h3>
                <p className="text-sky-100 text-sm mt-1">
                  Generate reports by date range and report type
                </p>
              </div>
              <svg 
                className={`h-5 w-5 text-white transform transition-transform ${showPayrollReports ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {showPayrollReports && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ReportTypeSelector 
                  value={reportType} 
                  onChange={handleReportTypeChange} 
                />
                
                <div className="lg:col-span-2">
                  <DateRangeSelector
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                  />
                </div>
              </div>

              {/* Area Filter Section */}
              <div className="border-t pt-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <PayrollAreaSelector
                    selectedAreas={selectedPayrollAreas}
                    onChange={setSelectedPayrollAreas}
                  />
                </div>
              </div>

              {reportType === ReportType.INSPECTOR_PROPERTY_MANAGER && (
                <div className="border-t pt-6">
                  <RoleFilterSelector
                    value={roleFilter}
                    onChange={setRoleFilter}
                    reportType={reportType}
                  />
                </div>
              )}

              <div className="border-t pt-6">
                <button
                  onClick={handleGenerateReport}
                  disabled={isGenerating || reportType === ReportType.WORK_ORDERS}
                  className="w-full md:w-auto px-6 py-3 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? 'Generating Report...' : 'Generate Report'}
                </button>
                {generateError && (
                  <p className="mt-2 text-sm text-red-600">
                    Error: {generateError.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Service Based Reports — Admin/Guest App Manager only */}
      {!isWorkOrdersSection && isAdmin && (
        <div className="bg-white rounded-lg shadow">
          <div 
            className="bg-sky-900 text-white p-6 cursor-pointer hover:bg-sky-800 transition-colors"
            onClick={() => setShowServiceReports(!showServiceReports)}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Service Based Reports</h3>
                <p className="text-sky-100 text-sm mt-1">
                  Use additional filters for more specific reports
                </p>
              </div>
              <svg 
                className={`h-5 w-5 text-white transform transition-transform ${showServiceReports ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {showServiceReports && (
            <div className="p-6 space-y-6">
              <AdvancedFiltersSection
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedServiceTypes={selectedServiceTypes}
                onServiceTypeChange={handleServiceTypeChange}
                selectedAreas={selectedAreas}
                onAreaChange={handleAreaChange}
                userRole="ADMIN"
                isRoleFilterEnabled={isRoleFilterEnabled}
                onRoleFilterToggle={handleRoleFilterToggle}
                resetFilters={resetAdvancedFilters}
                onApplyFilters={handleAdvancedReportGeneration}
                isGenerating={isGenerating}
                selectedProperties={selectedProperties}
                onSelectedPropertiesChange={handleSelectedPropertiesChange}
                selectedTeamMembers={selectedTeamMembers}
                onSelectedTeamMembersChange={handleSelectedTeamMembersChange}
                startDate={advancedStartDate}
                endDate={advancedEndDate}
                onStartDateChange={setAdvancedStartDate}
                onEndDateChange={setAdvancedEndDate}
                selectedJobStatuses={selectedJobStatuses}
                onJobStatusChange={handleJobStatusChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Work Order report generation lives on its own Reporting tab. */}
      {isWorkOrdersSection && (
      <div className="bg-white rounded-lg shadow">
        <div 
          className="bg-purple-900 text-white p-6 cursor-pointer hover:bg-purple-800 transition-colors"
          onClick={() => setShowWorkOrderReports(!showWorkOrderReports)}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium">Work Order Report Filters</h3>
              <p className="text-purple-100 text-sm mt-1">
                  Choose a date range, status, department, area, customer, property, or property manager
              </p>
            </div>
            <svg 
              className={`h-5 w-5 text-white transform transition-transform ${showWorkOrderReports ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {showWorkOrderReports && (
          <WorkOrderFilterSection
            startDate={workOrderStartDate}
            endDate={workOrderEndDate}
            onStartDateChange={setWorkOrderStartDate}
            onEndDateChange={setWorkOrderEndDate}
            selectedStatuses={selectedWorkOrderStatuses}
            onStatusesChange={setSelectedWorkOrderStatuses}
            selectedDepartments={selectedWorkOrderDepartments}
            onDepartmentsChange={setSelectedWorkOrderDepartments}
            selectedAreas={selectedWorkOrderAreas}
            onAreasChange={setSelectedWorkOrderAreas}
            selectedCustomers={selectedWorkOrderCustomers}
            onCustomersChange={setSelectedWorkOrderCustomers}
            selectedProperties={selectedWorkOrderProperties}
            onPropertiesChange={setSelectedWorkOrderProperties}
            selectedPropertyManagement={selectedWorkOrderPropertyManagement}
            onPropertyManagementChange={setSelectedWorkOrderPropertyManagement}
            onGenerate={handleGenerateWorkOrderReport}
            isGenerating={isGenerating}
          />
        )}
      </div>
      )}

      {/* Saved Reports Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 pb-0">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Saved Reports</h3>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-x-4 sm:gap-x-8" aria-label="Tabs">
              {!isWorkOrdersSection && isAdmin && (
                <button
                  onClick={() => setSavedReportsTab('housekeeping')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                    savedReportsTab === 'housekeeping'
                      ? 'border-sky-500 text-sky-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Housekeeping Reports
                  {savedReports?.reports && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      savedReportsTab === 'housekeeping' ? 'bg-sky-100 text-sky-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {savedReports.reports.filter(r => r && r.id && (r.type === ReportType.HOUSEKEEPING || r.type === ReportType.INSPECTOR_PROPERTY_MANAGER) && !r.name?.includes('Service Report:')).length}
                    </span>
                  )}
                </button>
              )}
              {!isWorkOrdersSection && isAdmin && (
                <button
                  onClick={() => setSavedReportsTab('service')}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                    savedReportsTab === 'service'
                      ? 'border-sky-500 text-sky-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Service Based Reports
                  {savedReports?.reports && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      savedReportsTab === 'service' ? 'bg-sky-100 text-sky-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {savedReports.reports.filter(r => r && r.id && r.name?.includes('Service Report:')).length}
                    </span>
                  )}
                </button>
              )}
              {isWorkOrdersSection && (
              <button
                onClick={() => setSavedReportsTab('workorders')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                  savedReportsTab === 'workorders'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Work Order Reports
                {savedReports?.reports && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    savedReportsTab === 'workorders' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {savedReports.reports.filter(r => r && r.id && r.type === ReportType.WORK_ORDERS).length}
                  </span>
                )}
              </button>
              )}
            </nav>
          </div>
        </div>

        <div className="p-6 pt-4">
        {savedReportsLoading ? (
          <p className="text-gray-500">Loading saved reports...</p>
        ) : savedReportsError ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-600">Error loading saved reports. Please refresh the page to try again.</p>
          </div>
          ) : (() => {
            // Filter reports based on selected tab
            const filteredReports = savedReports?.reports?.filter(report => {
              if (!report || !report.id) return false;
              
              if (savedReportsTab === 'housekeeping') {
                return (report.type === ReportType.HOUSEKEEPING || report.type === ReportType.INSPECTOR_PROPERTY_MANAGER) && 
                       !report.name?.includes('Service Report:');
              } else if (savedReportsTab === 'service') {
                return report.name?.includes('Service Report:');
              } else if (savedReportsTab === 'workorders') {
                return report.type === ReportType.WORK_ORDERS;
              }
              return false;
            }) || [];

            return filteredReports.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[300px]">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                    {filteredReports.map((report) => (
                  <tr key={report.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-[300px]">
                      {editingReportId === report.id ? (
                        // Editing mode - show input field
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editingReportName}
                            onChange={handleReportNameChange}
                            onKeyDown={(e) => handleKeyDownEditName(e, report.id)}
                            onBlur={() => handleSaveReportName(report.id)}
                            autoFocus
                            disabled={isUpdatingName}
                            className="flex-1 px-2 py-1 text-sm border border-sky-500 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                            placeholder="Enter report name"
                          />
                          <button
                            onClick={handleCancelEditingName}
                            disabled={isUpdatingName}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            title="Cancel (Esc)"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        // View mode - show name with edit button
                        <div className="flex items-center justify-between group">
                          <div className="truncate flex-1 pr-2">
                            {report.name}
                          </div>
                          <button
                            onClick={() => handleStartEditingName(report.id, report.name)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-sky-600 p-1 flex-shrink-0"
                            title="Edit name"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {safeFormatDate(report.startDate)} - {safeFormatDate(report.endDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {safeFormatDate(report.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleLoadSavedReport(report)}
                              className={`font-medium ${savedReportsTab === 'workorders' ? 'text-purple-600 hover:text-purple-900' : 'text-sky-600 hover:text-sky-900'}`}
                        >
                          View/Edit
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => handleDeleteReport(report.id, report.name)}
                          className="text-red-600 hover:text-red-900 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-gray-500">
                  No {savedReportsTab === 'housekeeping' ? 'housekeeping' : savedReportsTab === 'service' ? 'service based' : 'work order'} reports saved yet.
                </p>
              </div>
            );
          })()}
        </div>
      </div>

    </div>
  );
}
